import React, { useContext, useEffect, useMemo, useRef, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Card,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Grid,
  IconButton,
} from '@mui/material';
import PlayCircleOutlineIcon from '@mui/icons-material/PlayCircleOutline';
import SendIcon from '@mui/icons-material/Send';
import { AxiosError } from 'axios';
import DataTable from '../../../sharedModules/dataTable';
import PageHeader from '../../../sharedModules/layout/PageHeader';
import { SnackBarContext } from '../../../sharedModules/form/contexts/SnackBarContext';
import {
  CollectionsCorporateLocation,
} from '../../navigation/components/CollectionsLocationToggle';
import { useCollectionsLocation } from '../../shared/useCollectionsLocation';
import { useCollectionConfig } from '../../config/hooks/useCollectionConfig';
import { useCollectionScheduledEmails } from '../hooks/useCollectionScheduledEmails';
import { SendNowErrorBody, useSendCollectionEmail } from '../hooks/useSendCollectionEmail';
import { useResumeAutomatic } from '../hooks/useResumeAutomatic';
import { CollectionScheduledEmail } from '../model';
import ScheduledEmailModal from '../components/ScheduledEmailModal';
import ScheduledEmailFilters from '../components/ScheduledEmailFilters';
import ScheduleStatusCell from '../components/ScheduleStatusCell';
import { ScheduledEmailTableTooltip, scheduledEmailTooltipProps } from '../components/ScheduledEmailTableTooltip';
import { buildDepartmentGroupOptions } from '../../shared/departmentGroup/departmentGroup';
import { useDepartmentGroupDefault } from '../../shared/departmentGroup/useDepartmentGroupDefault';
import {
  EMPTY_SCHEDULED_EMAIL_FILTERS,
  filterScheduledEmails,
  ScheduledEmailFilterFields,
} from '../filterScheduledEmails';
import { relativeTimeUntil, toneChipProps } from '../utils';

type ConfirmAction = 'send' | 'resume';

interface ConfirmState {
  open: boolean;
  action: ConfirmAction;
  email: CollectionScheduledEmail | null;
  overrideBody?: string;
}

interface LocationTabProps {
  location: CollectionsCorporateLocation;
  enabled: boolean;
}

interface ScheduledEmailSendActionProps {
  row: CollectionScheduledEmail;
  isSending: boolean;
  sendingDisabled: boolean;
  onSendNow: (email: CollectionScheduledEmail) => void;
}

const getManualSendTooltip = (row: CollectionScheduledEmail): string => {
  if (row.schedule_status === 'manual_locked') {
    return `Manual send available in ${relativeTimeUntil(row.manual_send_locked_until)}`;
  }
  if (row.schedule_status === 'no_recipients') {
    return 'No email addresses';
  }
  return 'Manual send unavailable';
};

const ScheduledEmailSendAction: React.FC<ScheduledEmailSendActionProps> = ({
  row,
  isSending,
  sendingDisabled,
  onSendNow,
}) => {
  if (sendingDisabled) {
    const tooltipTitle = 'Email sending is disabled for this location.';
    return (
      <span {...scheduledEmailTooltipProps(tooltipTitle)} style={{ display: 'inline-flex' }}>
        <IconButton size="small" aria-label={tooltipTitle} disabled sx={{ cursor: 'default' }}>
          <SendIcon fontSize="small" />
        </IconButton>
      </span>
    );
  }

  if (!row.can_send_manually) {
    const tooltipTitle = getManualSendTooltip(row);
    return (
      <span {...scheduledEmailTooltipProps(tooltipTitle)} style={{ display: 'inline-flex' }}>
        <IconButton size="small" aria-label={tooltipTitle} disabled sx={{ cursor: 'default' }}>
          <SendIcon fontSize="small" />
        </IconButton>
      </span>
    );
  }

  const tooltipTitle = isSending ? 'Sending...' : 'Send Now';

  return (
    <span {...scheduledEmailTooltipProps(tooltipTitle)} style={{ display: 'inline-flex' }}>
      <IconButton
        size="small"
        color="primary"
        aria-label={tooltipTitle}
        onClick={(e) => {
          e.stopPropagation();
          onSendNow(row);
        }}
        disabled={isSending}
      >
        {isSending ? <CircularProgress size={16} /> : <SendIcon fontSize="small" />}
      </IconButton>
    </span>
  );
};

interface ResumeActionProps {
  row: CollectionScheduledEmail;
  isResuming: boolean;
  onResume: (email: CollectionScheduledEmail) => void;
}

const ResumeAction: React.FC<ResumeActionProps> = ({ row, isResuming, onResume }) => {
  const canResume = row.schedule_status === 'auto_skipped' || row.schedule_status === 'auto_paused';
  if (!canResume) return null;

  const tooltipTitle = isResuming ? 'Resuming...' : 'Resume automatic sending';

  return (
    <span {...scheduledEmailTooltipProps(tooltipTitle)} style={{ display: 'inline-flex' }}>
      <IconButton
        size="small"
        color="primary"
        aria-label={tooltipTitle}
        onClick={(e) => {
          e.stopPropagation();
          onResume(row);
        }}
        disabled={isResuming}
      >
        {isResuming ? <CircularProgress size={16} /> : <PlayCircleOutlineIcon fontSize="small" />}
      </IconButton>
    </span>
  );
};

const SCHEDULE_STATUS_SORT_ORDER: Record<CollectionScheduledEmail['schedule_status'], number> = {
  ready: 0,
  manual_locked: 1,
  auto_skipped: 2,
  auto_paused: 3,
  no_recipients: 4,
};

const TONE_SORT_ORDER: Record<CollectionScheduledEmail['tone'], number> = {
  past_due: 0,
  friendly: 1,
  second_notice: 2,
  urgent: 3,
};

const buildColumns = (
  isSending: boolean,
  isResuming: boolean,
  sendingDisabled: boolean,
  onSendNow: (email: CollectionScheduledEmail) => void,
  onResume: (email: CollectionScheduledEmail) => void,
) => [
  { name: 'Company', selector: 'company', sortable: true, grow: 2, minWidth: '280px' },
  {
    name: 'Tone',
    selector: 'tone',
    sortable: true,
    sortFunction: (a: CollectionScheduledEmail, b: CollectionScheduledEmail) =>
      TONE_SORT_ORDER[a.tone] - TONE_SORT_ORDER[b.tone],
    cell: (row: CollectionScheduledEmail) => <Chip {...toneChipProps(row.tone)} size="small" />,
    width: '160px',
  },
  { name: 'Subject', selector: 'subject', sortable: true, grow: 3, minWidth: '220px' },
  {
    name: 'Next Send',
    selector: 'next_send_at',
    sortable: true,
    sortFunction: (a: CollectionScheduledEmail, b: CollectionScheduledEmail) => {
      const aTime = a.next_send_at ? new Date(a.next_send_at).getTime() : Number.POSITIVE_INFINITY;
      const bTime = b.next_send_at ? new Date(b.next_send_at).getTime() : Number.POSITIVE_INFINITY;
      return aTime - bTime;
    },
    cell: (row: CollectionScheduledEmail) => row.next_send_at ?? '—',
    width: '180px',
  },
  {
    name: 'Status',
    selector: 'schedule_status',
    sortable: true,
    sortFunction: (a: CollectionScheduledEmail, b: CollectionScheduledEmail) =>
      SCHEDULE_STATUS_SORT_ORDER[a.schedule_status] - SCHEDULE_STATUS_SORT_ORDER[b.schedule_status],
    cell: (row: CollectionScheduledEmail) => <ScheduleStatusCell row={row} />,
    width: '240px',
    allowOverflow: true,
    ignoreRowClick: true,
  },
  {
    name: '',
    cell: (row: CollectionScheduledEmail) => (
      <Box sx={{ display: 'inline-flex', gap: 0.5 }}>
        <ResumeAction row={row} isResuming={isResuming} onResume={onResume} />
        <ScheduledEmailSendAction
          row={row}
          isSending={isSending}
          sendingDisabled={sendingDisabled}
          onSendNow={onSendNow}
        />
      </Box>
    ),
    width: '110px',
    allowOverflow: true,
    ignoreRowClick: true,
  },
];

const LocationTab: React.FC<LocationTabProps> = ({ location, enabled }) => {
  const { data, isLoading, isError, refetch } = useCollectionScheduledEmails(location, enabled);
  const { data: configResp } = useCollectionConfig(location, enabled);
  // Only treat as disabled once loaded and explicitly false — avoids a banner flash during load.
  const sendingDisabled = configResp?.data?.sending_enabled === false;
  const { sendEmail, isLoading: isSending } = useSendCollectionEmail();
  const { resumeAutomatic, isLoading: isResuming } = useResumeAutomatic();
  const { showSuccessSnackBar, showErrorSnackBar } = useContext(SnackBarContext);
  const { user, myGroups, defaultDepartmentGroup, myGroupsLabel } = useDepartmentGroupDefault();
  const [emails, setEmails] = useState<CollectionScheduledEmail[]>([]);
  const [filters, setFilters] = useState<ScheduledEmailFilterFields>(EMPTY_SCHEDULED_EMAIL_FILTERS);
  const [selectedEmail, setSelectedEmail] = useState<CollectionScheduledEmail | null>(null);
  const [confirm, setConfirm] = useState<ConfirmState>({ open: false, action: 'send', email: null });

  const filteredEmails = useMemo(() => filterScheduledEmails(emails, filters, myGroups), [emails, filters, myGroups]);

  const departmentGroupOptions = useMemo(() => buildDepartmentGroupOptions(emails, myGroups), [emails, myGroups]);

  const handleClearFilters = () =>
    setFilters({ ...EMPTY_SCHEDULED_EMAIL_FILTERS, departmentGroup: defaultDepartmentGroup });

  React.useEffect(() => {
    if (data?.data) setEmails(data.data);
  }, [data]);

  const appliedForUserRef = useRef<string | null>(null);
  useEffect(() => {
    if (!user?.id || appliedForUserRef.current === user.id) return;
    appliedForUserRef.current = user.id;
    setFilters((prev) => ({ ...prev, departmentGroup: defaultDepartmentGroup }));
  }, [user, defaultDepartmentGroup]);

  const handleSendNow = (email: CollectionScheduledEmail, overrideBody?: string) => {
    setSelectedEmail(null);
    setConfirm({ open: true, action: 'send', email, overrideBody });
  };

  const handleResumeAutomatic = (email: CollectionScheduledEmail) => {
    setSelectedEmail(null);
    setConfirm({ open: true, action: 'resume', email });
  };

  const closeConfirm = () => setConfirm({ open: false, action: 'send', email: null });

  const handleConfirm = async () => {
    if (!confirm.email) return;
    const target = confirm.email;
    const action = confirm.action;
    const overrideBody = confirm.overrideBody;
    closeConfirm();
    try {
      if (action === 'send') {
        await sendEmail({
          location: target.location,
          bill_code: target.bill_code,
          tone: target.tone,
          ...(overrideBody ? { override_body: overrideBody } : {}),
        });
        await refetch();
        showSuccessSnackBar?.(`Sent ${target.tone_label} email to ${target.company}.`);
      } else {
        await resumeAutomatic({ location: target.location, bill_code: target.bill_code });
        await refetch();
        showSuccessSnackBar?.(`Resumed automatic sending for ${target.company}.`);
      }
    } catch (err) {
      const axiosErr = err as AxiosError<SendNowErrorBody>;
      const body = axiosErr.response?.data;
      const fallback =
        action === 'send'
          ? 'Failed to send email. Please try again.'
          : 'Failed to resume automatic sending. Please try again.';
      const message = body?.error || fallback;
      showErrorSnackBar?.(message);
    }
  };

  if (isLoading) return <Box sx={{ p: 4, textAlign: 'center' }}>Loading...</Box>;
  if (isError)
    return (
      <Box sx={{ p: 4, textAlign: 'center', color: 'error.main' }}>
        Failed to load scheduled emails. Make sure a configuration exists for this location.
      </Box>
    );

  const confirmTitle = confirm.action === 'send' ? 'Send Email Now?' : 'Resume Automatic Sending?';
  const confirmBody =
    confirm.action === 'send' ? (
      <>
        Send the {confirm.email?.tone_label} collection email to <strong>{confirm.email?.company}</strong> now? This
        cannot be undone.
      </>
    ) : (
      <>
        Re-enable automatic sending for <strong>{confirm.email?.company}</strong>? The next automatic email will be
        scheduled for the next slot from today.
      </>
    );
  const confirmCta = confirm.action === 'send' ? 'Send' : 'Resume';
  const confirmDisabled = confirm.action === 'send' ? isSending : isResuming;

  const noDataMessage =
    emails.length === 0 ? 'No scheduled emails for this location.' : 'No scheduled emails match the current filters.';

  return (
    <>
      <ScheduledEmailTableTooltip />
      {sendingDisabled && (
        <Alert severity="warning" sx={{ mb: 2 }}>
          Email sending is disabled for {location}. Scheduled and manual sends are paused. Enable it under Collections →
          Configuration.
        </Alert>
      )}
      <ScheduledEmailFilters
        filters={filters}
        onChange={setFilters}
        onClear={handleClearFilters}
        departmentGroupOptions={departmentGroupOptions}
        myGroups={myGroups}
        myGroupsLabel={myGroupsLabel}
        defaultDepartmentGroup={defaultDepartmentGroup}
      />
      <Card sx={{ mt: 2.5 }}>
        <DataTable
          keyField="bill_code"
          columns={buildColumns(isSending, isResuming, sendingDisabled, handleSendNow, handleResumeAutomatic)}
          data={filteredEmails}
          pagination
          paginationPerPage={25}
          paginationRowsPerPageOptions={[25, 50, 100]}
          highlightOnHover
          striped
          noHeader
          onRowClicked={(row: CollectionScheduledEmail) => setSelectedEmail(row)}
          pointerOnHover
          conditionalRowStyles={[
            {
              when: (row: CollectionScheduledEmail) =>
                row.schedule_status === 'auto_skipped' || row.schedule_status === 'auto_paused',
              style: { backgroundColor: '#f5f5f5' },
            },
          ]}
          noDataComponent={<Box sx={{ p: 3, color: 'text.secondary' }}>{noDataMessage}</Box>}
        />
      </Card>

      <ScheduledEmailModal
        email={selectedEmail}
        sendingDisabled={sendingDisabled}
        onClose={() => setSelectedEmail(null)}
        onSendNow={handleSendNow}
        onResumeAutomatic={handleResumeAutomatic}
      />

      <Dialog open={confirm.open} onClose={closeConfirm}>
        <DialogTitle>{confirmTitle}</DialogTitle>
        <DialogContent>
          <DialogContentText>{confirmBody}</DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={closeConfirm}>Cancel</Button>
          <Button variant="contained" color="primary" onClick={handleConfirm} disabled={confirmDisabled}>
            {confirmDisabled ? <CircularProgress size={16} /> : confirmCta}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

const CollectionScheduledEmailsPage: React.FC = () => {
  const location = useCollectionsLocation();

  if (!location) {
    return (
      <Grid container>
        <Grid item xs={12}>
          <PageHeader mainTitle="Collections — Scheduled Emails" titleIcon={<SendIcon />} />
        </Grid>
        <Grid item xs={12} sx={{ mt: 2 }}>
          <Alert severity="info">No Collections location is assigned to this account.</Alert>
        </Grid>
      </Grid>
    );
  }

  return (
    <Grid container>
      <Grid item xs={12}>
        <PageHeader mainTitle="Collections — Scheduled Emails" titleIcon={<SendIcon />} />
      </Grid>
      <Grid item xs={12} sx={{ mt: 2 }}>
        <LocationTab location={location} enabled />
      </Grid>
    </Grid>
  );
};

export default CollectionScheduledEmailsPage;
